import { apiClass } from '@/base/class/api.class'
import { ENDPOINTS } from '@/base/const/endpoints.const'
import { ToolsClass } from '@/base/class/tools.class'
import { ADS_BANNER_SIZE, ADS_SOURCE_ID } from '@/base/const/ads.const'
import { REPORT_LEVEL, reportMessage } from '@/base/utils/errorReporting'

const state = {
  isAdDataLoaded: false,
  data: null,
  size: ADS_BANNER_SIZE.desktop,
  showCommercialBreake: false,
  commercialBreakClosed: false,
  serviceId: null,
  email: null,
  // Transakcja, dla której lista kreacji jest już pobrana. `AdsBanner` montuje się ponownie po każdym
  // powrocie do języka `pl` (`v-if="isDefaultLangActive"`), a bez tego każdy remount pytał adserwer
  // drugi raz o tę samą transakcję.
  loadedTransRemoteID: null,
  // Stan potwierdzeń wyświetlenia per `creationId`: `{ status, attempts }`. Trzymany w magazynie,
  // a nie w komponencie, bo zmienna w setupie zerowała się przy remouncie i ta sama kreacja
  // dostawała drugie potwierdzenie. Wspólny dla banera i commercial breaku.
  viewConfirmations: {},
}
const mutations = {
  SET_ADS_DATA(state, data) {
    state.data = data
  },
  SET_ADS_SIZE(state, value) {
    state.size = value
  },
  SET_ADS_SHOW_COMMERCIAL_BREAKE(state, value) {
    state.showCommercialBreake = value
  },
  SET_ADS_COMMERCIAL_BREAK_CLOSED(state, value) {
    state.commercialBreakClosed = value
  },
  SET_ADS_IS_AD_DATA_LOADED(state, value) {
    state.isAdDataLoaded = value
  },
  SET_ADS_SERVICE_ID(state, value) {
    state.serviceId = value
  },
  SET_ADS_EMAIL(state, value) {
    state.email = value
  },
  SET_ADS_LOADED_TRANS_REMOTE_ID(state, value) {
    state.loadedTransRemoteID = value
  },
  SET_ADS_VIEW_CONFIRMATION(state, { creationId, status, attempts }) {
    state.viewConfirmations = { ...state.viewConfirmations, [creationId]: { status, attempts } }
  },
}

/**
 * Ile razy wolno wysłać potwierdzenie wyświetlenia jednej kreacji, licząc nieudane próby.
 *
 * Po porażce kolejna próba idzie przy następnym zdarzeniu widoczności (scroll, resize, render).
 * Limit chroni adserwer przed żądaniem przy każdym `scroll`, gdy jest niedostępny.
 */
export const ADS_VIEW_CONFIRMATION_MAX_ATTEMPTS = 2

/**
 * Metoda budująca adres endpointu adserwera z zakodowanym query.
 *
 * Szablon bez kodowania psuł adresy e-mail: `+` docierał jako spacja, a `&` i `#` rozbijały query
 * (adserwer gubił `transaction_id`). Brak wartości idzie jako pusty parametr, nie `undefined`.
 */
function buildAdsEndpoint(path, params) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    query.append(key, value === null || value === undefined ? '' : String(value))
  })
  return `${path}?${query}`
}

const actions = {
  getAdsBanners({ commit, state }, { serviceId, transRemoteID, paywayId, email }) {
    if (state.loadedTransRemoteID && state.loadedTransRemoteID === transRemoteID) {
      return Promise.resolve()
    }
    commit('SET_ADS_SERVICE_ID', serviceId)
    commit('SET_ADS_EMAIL', email)
    const endpoint = buildAdsEndpoint(ENDPOINTS.ads.getBannersList, {
      serviceId,
      sourceId: ADS_SOURCE_ID,
      paywayId,
      email,
      transaction_id: transRemoteID,
    })
    return apiClass
      .getAds(endpoint)
      .then((response) => {
        commit('SET_ADS_DATA', response.body)
        commit('SET_ADS_LOADED_TRANS_REMOTE_ID', transRemoteID)
        if (ToolsClass.isMobileScreenSize()) {
          commit('SET_ADS_SIZE', ADS_BANNER_SIZE.mobile)
        }
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
      .finally(() => {
        commit('SET_ADS_IS_AD_DATA_LOADED', true)
      })
  },
  /**
   * Potwierdzenie wyświetlenia kreacji - najwyżej jedno udane na `creationId`.
   *
   * Pominięte wywołanie (już potwierdzone, w toku, wyczerpany limit prób) rozwiązuje się bez żądania.
   * Kreacja jest oznaczana jako potwierdzona dopiero po sukcesie: wcześniej flagę ustawiało `finally`,
   * więc błąd sieci gubił wyświetlenie na dobre. Odrzucenie nie jest raportowane - awaria pixela
   * nie jest zgłoszeniem.
   */
  sendAdsBannerViewConfirmation({ commit, state }, { creationId, transRemoteID }) {
    const current = state.viewConfirmations[creationId]
    const attempts = current?.attempts ?? 0
    if (current?.status === 'confirmed' || current?.status === 'pending') {
      return Promise.resolve()
    }
    if (attempts >= ADS_VIEW_CONFIRMATION_MAX_ATTEMPTS) {
      return Promise.resolve()
    }
    commit('SET_ADS_VIEW_CONFIRMATION', { creationId, status: 'pending', attempts: attempts + 1 })
    const endpoint = buildAdsEndpoint(ENDPOINTS.ads.sendBannerViewConfirmation, {
      serviceId: state.serviceId,
      creationId,
      email: state.email,
      transaction_id: transRemoteID,
    })
    return apiClass.getAds(endpoint).then(
      (response) => {
        commit('SET_ADS_VIEW_CONFIRMATION', { creationId, status: 'confirmed', attempts: attempts + 1 })
        return response
      },
      (error) => {
        commit('SET_ADS_VIEW_CONFIRMATION', { creationId, status: 'failed', attempts: attempts + 1 })
        return Promise.reject(error)
      }
    )
  },
}
/**
 * Serwisy, dla których nieoczekiwany kształt odpowiedzi `/ads` został już zgłoszony.
 *
 * Gettery reklamowe są czytane z kilku komponentów przy każdym renderze, więc bez tego jedna
 * odpowiedź generowałaby serię identycznych zdarzeń. Klucz to `serviceId`, bo pytanie brzmi
 * „ilu serwisom adserver odpowiada czymś, co nie jest listą", a nie „ile razy przeliczył się getter".
 */
const reportedUnexpectedAdsData = new Set()

/**
 * Metoda zgłaszająca odpowiedź adserwera, która nie jest listą banerów.
 *
 * Po dołożeniu `getBannerList` ten stan przestał rzucać, czyli **przestał być widoczny w Sentry sam
 * z siebie** - a odpowiada za niewyświetlone reklamy, których nikt inny nie policzy.
 */
function reportUnexpectedAdsData(state) {
  const key = String(state.serviceId ?? null)
  if (reportedUnexpectedAdsData.has(key)) {
    return
  }
  reportedUnexpectedAdsData.add(key)
  reportMessage('Ads: odpowiedź adserwera nie jest listą banerów', {
    level: REPORT_LEVEL.warning,
    tags: { flow: 'ads' },
    extra: {
      serviceId: state.serviceId ?? null,
      // Typ i `length` rozstrzygają, czy backend przysyła string (np. HTML błędu), czy obiekt
      // opakowujący listę - bez tego nie da się napisać zgłoszenia do adserwera.
      typDanych: typeof state.data,
      czyTablica: Array.isArray(state.data),
      dlugosc: state.data?.length ?? null,
    },
  })
}

/**
 * Lista banerów z odpowiedzi adserwera.
 *
 * `SET_ADS_DATA` zapisuje `response.body` bez sprawdzenia typu, a strażnik `!state.data.length`
 * przepuszczał wszystko, co ma `length` - w tym string. Wtedy `state.data.find` nie istnieje
 * i getter rzucał `data.find is not a function` (6 grup w Sentry na 2 doby: komunikat per silnik
 * razy hashowana nazwa chunka `useApp.*`, więc każda z nich wyglądała na pojedynczy incydent).
 * `isAdsBannerDataSet` miał `Array.isArray` od początku - pozostałe trzy gettery nie.
 */
const getBannerList = (state) => {
  if (Array.isArray(state.data)) {
    return state.data
  }
  if (state.data) {
    reportUnexpectedAdsData(state)
  }
  return []
}

/**
 * Pierwsza kreacja na bieżący rozmiar ekranu, osobno dla banera i dla commercial breaku.
 *
 * Commercial break to wyłącznie `commercialBreak: true`; wszystko inne (także brak pola albo `null`)
 * jest zwykłym banerem. Przy porównaniu ścisłym z `false` kreacja bez tego pola nie trafiała nigdzie,
 * a raport kształtu odpowiedzi tego nie łapał, bo lista była poprawną tablicą.
 */
const findBanner = (state, commercialBreak) =>
  getBannerList(state).find(
    (banner) => banner.targetDevice === state.size && (banner.commercialBreak === true) === commercialBreak
  )

const getters = {
  isAdsBannerDataSet(state) {
    return getBannerList(state).length
  },
  isCommercialBreakDataSet(state) {
    return !!findBanner(state, true)
  },
  getBannerToDisplay(state) {
    return findBanner(state, false) ?? false
  },
  getCommercialBreak(state) {
    return findBanner(state, true) ?? false
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
